// src/pages/SuperAdmin/TemplateStudio.jsx
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import { get, put, post } from 'helpers/api_helper';
import { showSuccessAlert } from 'pages/utils/alertMessages';
import {
  openInvoicePrintWindow,
  openPrescriptionPrintWindow,
  openReceiptPrintWindow,
} from 'pages/utils/printHelpers';
import {
  RiPrinterLine,
  RiSaveLine,
  RiMedicineBottleLine,
  RiBillLine,
  RiShareForwardLine,
  RiCheckLine,
  RiToothLine,
  RiSparklingLine,
  RiLayoutMasonryLine,
  RiAwardLine,
  RiReceiptLine,
  RiSettings4Line,
  RiEyeLine,
  RiImageAddLine,
  RiDeleteBinLine,
  RiUpload2Line,
  RiToggleFill,
  RiToggleLine,
  RiRefreshLine,
  RiPaletteLine,
} from 'react-icons/ri';

import './templateStudio.scss';

const DEFAULT_PRESCRIPTION_THEMES = [
  {
    id: 1,
    type: 'prescription',
    theme_id: 'modern_clinical',
    title: 'Modern Clinical Pad',
    badge: 'Hospital Standard',
    accent_color: '#0284c7',
    tagline: 'Specialized Dental Surgery, Implantology & Oral Health Center',
    header_title: 'CLINICAL PRESCRIPTION',
    doctorName: 'Dr. Praveen Kumar, MDS',
    doctorDeg: 'Oral & Maxillofacial Surgeon | Implantologist',
    doctorReg: 'DCI-REG-88291',
    logoImage: '',
    signImage: '',
    general_advice: '• Take all prescribed medicines strictly at specified intervals.\n• Avoid chewing hard food on treated side for 3 days.\n• Start warm saline rinses 24 hours after surgery.\n• Contact clinic immediately in case of acute swelling or drug reaction.',
    footer_note: 'Valid for 15 days from date of issue. Computerized electronic medical record.',
    description: 'Crisp medical sky blue layout with patient vitals, diagnosis pill, and structured timing grid.',
    is_active: true,
    is_default: true,
  },
  {
    id: 2,
    type: 'prescription',
    theme_id: 'aesthetic_royal',
    title: 'Royal Aesthetic Studio',
    badge: 'Premium Cosmetic',
    accent_color: '#4f46e5',
    tagline: 'Center for Advanced Cosmetic Dentistry, Veneers & Aligners',
    header_title: 'SMILE ARCHITECTURE PRESCRIPTION',
    doctorName: 'Dr. Praveen Kumar, MDS',
    doctorDeg: 'Chief Cosmetic Dentist & Orthodontic Specialist',
    doctorReg: 'DCI-REG-88291',
    logoImage: '',
    signImage: '',
    general_advice: '• Wear aligners/nightguard as instructed by your orthodontist.\n• Rinse mouth with Hexidine after brushing.\n• Avoid dark colored beverages for 48 hours after whitening.',
    footer_note: 'Precision Digital Dentistry • ISO 9001 Certified Clinical Protocols',
    description: 'Royal indigo palette tailored for aesthetic dental studios and smile transformation clinics.',
    is_active: true,
    is_default: false,
  },
  {
    id: 3,
    type: 'prescription',
    theme_id: 'classic_letterhead',
    title: 'Executive Classic',
    badge: 'Letterhead Minimalist',
    accent_color: '#0f172a',
    tagline: 'Multi-Speciality Dental & Maxillofacial OPD Institute',
    header_title: 'DOCTOR S PRESCRIPTION PAD',
    doctorName: 'Dr. Praveen Kumar, MDS',
    doctorDeg: 'Senior Dental Surgeon & Consultant',
    doctorReg: 'DCI-REG-88291',
    logoImage: '',
    signImage: '',
    general_advice: '• Complete the full antibiotic course as prescribed.\n• Maintain strict oral hygiene.',
    footer_note: 'Printed from 909 Dental OPD Central Health Record System.',
    description: 'High-contrast minimalist dark layout suitable for pre-printed letterhead clinic stationery.',
    is_active: true,
    is_default: false,
  },
  {
    id: 4,
    type: 'prescription',
    theme_id: 'emerald_opd',
    title: 'Comprehensive OPD',
    badge: 'Dental OPD Dual',
    accent_color: '#059669',
    tagline: 'Oral Health, Endodontics & Preventive Dental Care',
    header_title: 'DENTAL OPD PRESCRIPTION',
    doctorName: 'Dr. Praveen Kumar, MDS',
    doctorDeg: 'Consultant Endodontist & Dental Surgeon',
    doctorReg: 'DCI-REG-88291',
    logoImage: '',
    signImage: '',
    general_advice: '• Do not chew on the root canal treated tooth until permanent crown placement.\n• Take pain relief medication strictly after food.',
    footer_note: 'Patient helpline: 098220 11111 • Email: appointments@oralstop.com',
    description: 'Emerald green theme optimized for routine restorative, periodontal, and endodontic patient cases.',
    is_active: true,
    is_default: false,
  },
];

const DEFAULT_INVOICE_THEMES = [
  {
    id: 5,
    type: 'invoice',
    theme_id: 'tax_invoice_standard',
    title: 'Tax Invoice (GST Standard)',
    badge: 'GST / Legal Compliant',
    accent_color: '#0284c7',
    header_title: 'INVOICE',
    tagline: 'Specialized Dental Care & Oral Health Center',
    gst_number: '29ABCDE1234F1Z5',
    signatory_title: 'Authorized Medical Officer',
    logoImage: '',
    signImage: '',
    terms: '1. Payments once made are non-refundable.\n2. Please preserve this invoice for insurance and tax claims.\n3. Follow-up appointments must be booked in advance.',
    show_bank_details: true,
    bank_name: 'HDFC Bank',
    account_no: '50200012345678',
    ifsc_code: 'HDFC0001234',
    branch: 'Koramangala, Bangalore',
    notes: 'Thank you for trusting 909 Dental OPD with your smile.',
    description: 'Comprehensive tax invoice featuring GSTIN registration, procedure HSN codes, NEFT bank wire details, and seal.',
    is_active: true,
    is_default: true,
  },
  {
    id: 6,
    type: 'invoice',
    theme_id: 'aesthetic_minimalist',
    title: 'Aesthetic Statement',
    badge: 'High-End Aesthetic',
    accent_color: '#0f172a',
    header_title: 'TREATMENT STATEMENT & INVOICE',
    tagline: 'Center for Aesthetic Smile Designing & Orthodontics',
    gst_number: '29AABCT1234F2Z8',
    signatory_title: 'Clinic Accounts Desk',
    logoImage: '',
    signImage: '',
    terms: '1. Treatment fee includes clinical procedures, lab fabrications, and follow-up reviews.\n2. Digital invoices are synced to your patient portal.',
    show_bank_details: false,
    notes: 'We are committed to delivering world-class dental excellence.',
    description: 'Sleek dark-accented modern layout tailored for premium cosmetic & aligner dental studios.',
    is_active: true,
    is_default: false,
  },
  {
    id: 7,
    type: 'invoice',
    theme_id: 'emerald_healthcare',
    title: 'Emerald Healthcare',
    badge: 'Family OPD',
    accent_color: '#059669',
    header_title: 'OFFICIAL MEDICAL INVOICE',
    tagline: 'Comprehensive Family Dental OPD & Implant Clinic',
    gst_number: '36ABCDE9876K1Z2',
    signatory_title: 'Reception & Cashier',
    logoImage: '',
    signImage: '',
    terms: '1. Verified computerized digital billing document.\n2. Contact accounts desk for insurance pre-authorization documentation.',
    show_bank_details: true,
    bank_name: 'State Bank of India',
    account_no: '304918239012',
    ifsc_code: 'SBIN0004521',
    branch: 'Indiranagar, Bangalore',
    notes: 'Thank you for your visit. Keep smiling!',
    description: 'Vibrant healthcare theme with clear payment status stamps and balance breakdown.',
    is_active: true,
    is_default: false,
  },
];

const DEFAULT_RECEIPT_THEMES = [
  {
    id: 8,
    type: 'receipt',
    theme_id: 'official_voucher',
    title: 'Official Money Receipt Voucher',
    badge: 'Financial Voucher',
    accent_color: '#059669',
    header_title: 'OFFICIAL MONEY RECEIPT',
    tagline: 'Payment Acknowledgement & Financial Voucher',
    signatory_title: 'Accounts Reception Desk',
    logoImage: '',
    signImage: '',
    notes: 'Received with thanks towards specialized dental treatment and clinical services.',
    footer_note: '• This is an authentic computerized digital payment receipt.\n• Thank you for trusting 909 Dental OPD with your smile.',
    description: 'Standard receipt voucher with payment method, invoice reference, and cashier signature line.',
    is_active: true,
    is_default: true,
  },
  {
    id: 9,
    type: 'receipt',
    theme_id: 'aesthetic_receipt',
    title: 'Aesthetic Studio Receipt',
    badge: 'Cosmetic & Aligners',
    accent_color: '#4f46e5',
    header_title: 'TREATMENT PAYMENT RECEIPT',
    tagline: 'Center for Advanced Aesthetic Smile Architecture',
    signatory_title: 'Accounts & Billing Desk',
    logoImage: '',
    signImage: '',
    notes: 'Received with thanks towards aesthetic smile transformation and orthodontic treatment.',
    footer_note: '• Computer-generated digital statement.\n• Please preserve this receipt for tax and reimbursement claims.',
    description: 'Royal indigo theme suited for high-end aesthetic dental studios and cosmetic cases.',
    is_active: true,
    is_default: false,
  },
];

const COLOR_PALETTES = [
  { name: 'Sky Medical Blue', value: '#0284c7' },
  { name: 'Royal Indigo', value: '#4f46e5' },
  { name: 'Emerald Mint', value: '#059669' },
  { name: 'Modern Purple', value: '#7c3aed' },
  { name: 'Executive Dark Slate', value: '#0f172a' },
  { name: 'Amber Gold', value: '#d97706' },
  { name: 'Crimson Rose', value: '#e11d48' },
];

const MOCK_RX_DATA = {
  patient_name: 'Mrs. Sunita Reddy',
  patient_id: '909-P-101',
  doctor_name: 'Dr. Praveen Kumar, MDS',
  doctor_qualification: 'Oral & Maxillofacial Surgeon | Implantologist',
  doctor_reg_no: 'DCI-REG-88291',
  date: new Date(),
  diagnosis: 'Acute Irreversible Pulpitis #16 • Chronic Marginal Gingivitis',
  patient: {
    title: 'Mrs.',
    first_name: 'Sunita',
    last_name: 'Reddy',
    age: 34,
    gender: 'Female',
    mobile: '+91 98765 43210',
    case_no: '909-P-101',
  },
  medicines: [
    { medicine: 'Tab. Augmentin 625mg', molucule: 'Amoxicillin 500mg + Clavulanic Acid 125mg', dose: '625mg', frequent: '1-0-1', duration: '5 days', instructions: 'After Food' },
    { medicine: 'Tab. Zerodol-SP', molucule: 'Aceclofenac + Paracetamol + Serratiopeptidase', dose: '1 Tab', frequent: '1-0-1', duration: '3 days', instructions: 'After Food' },
    { medicine: 'Cap. Pan-D', molucule: 'Pantoprazole 40mg + Domperidone 30mg SR', dose: '1 Cap', frequent: '1-0-0', duration: '5 days', instructions: 'Before Breakfast' },
    { medicine: 'Hexidine Mouthwash 0.2%', molucule: 'Chlorhexidine Gluconate 0.2% w/v', dose: '10ml', frequent: '1-0-1', duration: '7 days', instructions: 'Rinse for 30s' },
  ],
};

const MOCK_INVOICE_DATA = {
  id: 101,
  billing_no: 'INV-2026-0891',
  date: new Date(),
  patient_name: 'Mrs. Sunita Reddy',
  patient_mobile: '+91 98765 43210',
  patients: {
    title: 'Mrs.',
    first_name: 'Sunita',
    last_name: 'Reddy',
    case_no: '909-P-101',
    mobile: '+91 98765 43210',
  },
  treatment_info: JSON.stringify([
    { _type: 'Root Canal Treatment (RCT)', tooth: '16', cost: 4500, discount: 0, total: 4500, notes: 'Rotary instrumentation & Bio-ceramic obturation' },
    { _type: 'Monolithic Zirconia Crown Placement', tooth: '16', cost: 12000, discount: 1000, total: 11000, notes: '10-Year Warranty Card Included' },
    { _type: 'Ultrasonic Scaling & Polishing', tooth: 'Full Mouth', cost: 1500, discount: 0, total: 1500, notes: 'Subgingival plaque removal' },
  ]),
  total: 17000,
  paid: 17000,
  pending: 0,
  payment_mode: 'UPI / NetBanking',
};

const MOCK_RECEIPT_DATA = {
  id: 402,
  receipt_no: 'REC-909-402',
  billing_no: 'INV-2026-0891',
  amount: 17000,
  payment_mode: 'UPI (GPay / PhonePe)',
  payment_date: new Date(),
  remark: 'Full settlement for Root Canal Treatment & Zirconia Crown (#16)',
  patient: {
    title: 'Mrs.',
    first_name: 'Sunita',
    last_name: 'Reddy',
    case_no: '909-P-101',
    mobile: '+91 98765 43210',
  },
};

const TemplateStudio = () => {
  const [activeDocType, setActiveDocType] = useState('prescription');
  const [zoomLevel, setZoomLevel] = useState(100);

  // Dynamic template lists from DB
  const [dbTemplates, setDbTemplates] = useState({
    prescription: DEFAULT_PRESCRIPTION_THEMES,
    invoice: DEFAULT_INVOICE_THEMES,
    receipt: DEFAULT_RECEIPT_THEMES,
  });

  // Active configurations
  const [rxConfig, setRxConfig] = useState(DEFAULT_PRESCRIPTION_THEMES[0]);
  const [invConfig, setInvConfig] = useState(DEFAULT_INVOICE_THEMES[0]);
  const [recConfig, setRecConfig] = useState(DEFAULT_RECEIPT_THEMES[0]);

  const fetchDbTemplates = async () => {
    try {
      const res = await get('/superadmin/document-templates');
      if (res?.success && Array.isArray(res?.body) && res.body.length > 0) {
        const grouped = {
          prescription: res.body.filter((t) => t.type === 'prescription'),
          invoice: res.body.filter((t) => t.type === 'invoice'),
          receipt: res.body.filter((t) => t.type === 'receipt'),
        };
        setDbTemplates({
          prescription: grouped.prescription.length > 0 ? grouped.prescription : DEFAULT_PRESCRIPTION_THEMES,
          invoice: grouped.invoice.length > 0 ? grouped.invoice : DEFAULT_INVOICE_THEMES,
          receipt: grouped.receipt.length > 0 ? grouped.receipt : DEFAULT_RECEIPT_THEMES,
        });

        // Initialize active selections if available
        if (grouped.prescription.length > 0) setRxConfig(grouped.prescription[0]);
        if (grouped.invoice.length > 0) setInvConfig(grouped.invoice[0]);
        if (grouped.receipt.length > 0) setRecConfig(grouped.receipt[0]);
      }
    } catch (e) {
      console.warn('Failed to load DB templates:', e);
    }
  };

  useEffect(() => {
    fetchDbTemplates();
    try {
      const savedRx = localStorage.getItem('prescription_template_config');
      if (savedRx) setRxConfig(JSON.parse(savedRx));
      const savedInv = localStorage.getItem('invoice_template_config');
      if (savedInv) setInvConfig(JSON.parse(savedInv));
      const savedRec = localStorage.getItem('receipt_template_config');
      if (savedRec) setRecConfig(JSON.parse(savedRec));
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const handleToggleActiveStatus = async (e, theme) => {
    e.stopPropagation();
    const newStatus = !theme.is_active;
    try {
      if (theme.id) {
        await put(`/superadmin/document-templates/${theme.id}`, { is_active: newStatus });
      }
      setDbTemplates((prev) => ({
        ...prev,
        [theme.type || activeDocType]: prev[theme.type || activeDocType].map((t) =>
          (t.id === theme.id || t.theme_id === theme.theme_id) ? { ...t, is_active: newStatus } : t
        ),
      }));
      showSuccessAlert(`Template "${theme.title}" is now ${newStatus ? 'ACTIVE (Visible to all clients)' : 'INACTIVE (Hidden from clients)'}!`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleApplyRxPreset = (preset) => {
    const updated = {
      ...preset,
      logoImage: rxConfig.logoImage || preset.logoImage || '',
      signImage: rxConfig.signImage || preset.signImage || '',
      accentColor: preset.accent_color || preset.accentColor || '#0284c7',
      general_advice: preset.general_advice || preset.generalAdvice || rxConfig.general_advice,
      footer_note: preset.footer_note || preset.footerNote || rxConfig.footer_note,
    };
    setRxConfig(updated);
    localStorage.setItem('prescription_template_config', JSON.stringify(updated));
    showSuccessAlert(`Activated "${preset.title || preset.name}" Prescription Template!`);
  };

  const handleApplyInvPreset = (preset) => {
    const updated = {
      ...preset,
      logoImage: invConfig.logoImage || preset.logoImage || '',
      signImage: invConfig.signImage || preset.signImage || '',
      accentColor: preset.accent_color || preset.accentColor || '#0284c7',
      terms: preset.terms || invConfig.terms,
    };
    setInvConfig(updated);
    localStorage.setItem('invoice_template_config', JSON.stringify(updated));
    showSuccessAlert(`Activated "${preset.title || preset.name}" Invoice Template!`);
  };

  const handleApplyRecPreset = (preset) => {
    const updated = {
      ...preset,
      logoImage: recConfig.logoImage || preset.logoImage || '',
      signImage: recConfig.signImage || preset.signImage || '',
      accentColor: preset.accent_color || preset.accentColor || '#059669',
      notes: preset.notes || preset.thanksNote || recConfig.notes,
      footer_note: preset.footer_note || preset.footerNote || recConfig.footer_note,
    };
    setRecConfig(updated);
    localStorage.setItem('receipt_template_config', JSON.stringify(updated));
    showSuccessAlert(`Activated "${preset.title || preset.name}" Receipt Template!`);
  };

  const handleSaveCurrent = async () => {
    const current = activeDocType === 'prescription' ? rxConfig : (activeDocType === 'invoice' ? invConfig : recConfig);
    if (current.id) {
      try {
        await put(`/superadmin/document-templates/${current.id}`, current);
      } catch (e) {
        console.warn('Backend update failed:', e);
      }
    }

    if (activeDocType === 'prescription') {
      localStorage.setItem('prescription_template_config', JSON.stringify(rxConfig));
      showSuccessAlert('Prescription template configuration saved and synced to clients!');
    } else if (activeDocType === 'invoice') {
      localStorage.setItem('invoice_template_config', JSON.stringify(invConfig));
      showSuccessAlert('Invoice template configuration saved and synced to clients!');
    } else {
      localStorage.setItem('receipt_template_config', JSON.stringify(recConfig));
      showSuccessAlert('Receipt template configuration saved and synced to clients!');
    }
  };

  const handlePushAll = async () => {
    try {
      await post('/superadmin/seed-document-templates', {});
    } catch (e) { }
    localStorage.setItem('prescription_template_config', JSON.stringify(rxConfig));
    localStorage.setItem('invoice_template_config', JSON.stringify(invConfig));
    localStorage.setItem('receipt_template_config', JSON.stringify(recConfig));
    showSuccessAlert('✨ All document templates successfully synchronized & activated as default across all client accounts!');
  };

  const handleImageUpload = (e, fieldType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result;
      if (activeDocType === 'prescription') setRxConfig((prev) => ({ ...prev, [fieldType]: base64Data }));
      else if (activeDocType === 'invoice') setInvConfig((prev) => ({ ...prev, [fieldType]: base64Data }));
      else setRecConfig((prev) => ({ ...prev, [fieldType]: base64Data }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (fieldType) => {
    if (activeDocType === 'prescription') setRxConfig((prev) => ({ ...prev, [fieldType]: '' }));
    else if (activeDocType === 'invoice') setInvConfig((prev) => ({ ...prev, [fieldType]: '' }));
    else setRecConfig((prev) => ({ ...prev, [fieldType]: '' }));
  };

  const currentConfig = activeDocType === 'prescription' ? rxConfig : (activeDocType === 'invoice' ? invConfig : recConfig);
  const activeThemesList = dbTemplates[activeDocType] || [];

  return (
    <div className="template-studio-wrapper py-2">
      {/* 1. HERO BANNER */}
      <div
        className="card border-0 shadow-sm overflow-hidden mb-4"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0369a1 100%)',
          color: '#ffffff',
          borderRadius: '14px',
        }}
      >
        <div className="card-body p-4 position-relative">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1.5">
                <span className="badge bg-white text-dark font-size-11 font-weight-bold px-2.5 py-1 shadow-2xs d-inline-flex align-items-center gap-1">
                  <RiSparklingLine className="text-warning" /> SuperAdmin Template Governance
                </span>
                <span className="badge bg-soft-info text-info font-size-11 px-2 py-0.5 border border-info">
                  Live Client Sync
                </span>
              </div>
              <h3 className="font-size-22 font-weight-bold text-white mb-1">
                SuperAdmin Print & Document Template Studio
              </h3>
              <p className="text-light opacity-75 font-size-13 mb-0" style={{ maxWidth: '640px' }}>
                Manage document layouts for Clinical Prescription Pads (℞), Tax Invoices (🧾), and Money Receipts (💳). Activated templates immediately appear for selection in client accounts.
              </p>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                color="light"
                size="sm"
                className="font-size-12 font-weight-semibold shadow-sm d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                onClick={() => {
                  if (activeDocType === 'prescription') openPrescriptionPrintWindow(MOCK_RX_DATA, null, null, rxConfig);
                  else if (activeDocType === 'invoice') openInvoicePrintWindow(MOCK_INVOICE_DATA);
                  else openReceiptPrintWindow(MOCK_RECEIPT_DATA);
                }}
              >
                <RiPrinterLine className="font-size-14 text-primary" /> Test Print PDF
              </Button>

              <Button
                color="warning"
                size="sm"
                className="font-size-12 font-weight-bold shadow-sm d-inline-flex align-items-center gap-1.5 text-dark px-3 py-1.5"
                onClick={handlePushAll}
              >
                <RiShareForwardLine className="font-size-14" /> Push & Sync All Defaults to Clients
              </Button>
            </div>
          </div>

          <hr className="my-3.5 border-light opacity-20" />

          {/* Document Type Selector Pill Tabs */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="doc-type-pills">
              <button
                type="button"
                className={`pill-btn ${activeDocType === 'prescription' ? 'active' : ''}`}
                onClick={() => setActiveDocType('prescription')}
              >
                <RiMedicineBottleLine className="font-size-14" /> Prescription Pads (℞)
              </button>

              <button
                type="button"
                className={`pill-btn ${activeDocType === 'invoice' ? 'active' : ''}`}
                onClick={() => setActiveDocType('invoice')}
              >
                <RiBillLine className="font-size-14" /> Tax Invoices & Statements (🧾)
              </button>

              <button
                type="button"
                className={`pill-btn ${activeDocType === 'receipt' ? 'active' : ''}`}
                onClick={() => setActiveDocType('receipt')}
              >
                <RiReceiptLine className="font-size-14" /> Money Receipts (💳)
              </button>
            </div>

            <div className="d-flex align-items-center gap-2 text-light font-size-11 opacity-80">
              <RiAwardLine className="text-warning font-size-14" /> Multi-Tenant Central Repository
            </div>
          </div>
        </div>
      </div>

      {/* 2. TOP SECTION: SPACIOUS THEME CARDS GALLERY WITH ACTIVE/INACTIVE TOGGLES */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="font-size-15 font-weight-bold text-dark mb-0 d-flex align-items-center gap-2">
            <RiLayoutMasonryLine className="text-primary" />
            {activeDocType === 'prescription' ? 'Prescription Template Catalog' : (activeDocType === 'invoice' ? 'Tax Invoice Template Catalog' : 'Money Receipt Template Catalog')}
          </h5>
          <span className="text-muted font-size-12">
            SuperAdmin can toggle <strong>Active / Inactive</strong> status below to control client visibility.
          </span>
        </div>

        <div className="theme-grid">
          {activeThemesList.map((theme) => {
            const isSelected = (currentConfig.theme_id === theme.theme_id || currentConfig.id === theme.id);
            const accentColor = theme.accent_color || theme.accentColor || '#0284c7';
            const isActive = theme.is_active !== false;

            return (
              <div
                key={theme.id || theme.theme_id}
                className={`theme-card-box ${isSelected ? 'active-theme' : ''}`}
                onClick={() => {
                  if (activeDocType === 'prescription') handleApplyRxPreset(theme);
                  else if (activeDocType === 'invoice') handleApplyInvPreset(theme);
                  else handleApplyRecPreset(theme);
                }}
                style={{ opacity: isActive ? 1 : 0.65 }}
              >
                <div className="theme-top-row">
                  <div className="d-flex align-items-center gap-2">
                    <span className="theme-color-indicator" style={{ background: accentColor }} />
                    <span
                      className="badge font-size-10"
                      style={{
                        background: isSelected ? `${accentColor}18` : '#f1f5f9',
                        color: isSelected ? accentColor : '#475569',
                        border: `1px solid ${isSelected ? `${accentColor}40` : '#e2e8f0'}`,
                      }}
                    >
                      {theme.badge || 'Template Theme'}
                    </span>
                  </div>

                  {/* SuperAdmin Active Toggle */}
                  <button
                    type="button"
                    className={`btn btn-sm px-2 py-0.5 rounded-pill font-size-10 d-inline-flex align-items-center gap-1 ${isActive ? 'btn-soft-success font-weight-bold' : 'btn-soft-secondary'}`}
                    onClick={(e) => handleToggleActiveStatus(e, theme)}
                    title={isActive ? 'Click to deactivate for clients' : 'Click to activate for clients'}
                  >
                    {isActive ? <RiToggleFill className="text-success font-size-14" /> : <RiToggleLine className="text-muted font-size-14" />}
                    {isActive ? 'Active for Clients' : 'Inactive'}
                  </button>
                </div>

                <div className="theme-card-title">{theme.title || theme.name}</div>
                <div className="theme-card-desc">{theme.description}</div>

                <div className="theme-card-footer">
                  <span className="text-muted font-size-11 font-weight-semibold">
                    {isSelected ? '✓ Selected in Studio' : 'Click to Configure'}
                  </span>
                  <span className="badge bg-light text-dark border font-size-10">
                    {isActive ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. BOTTOM SECTION: SPACIOUS SPLIT (STUDIO & CANVAS) */}
      <Row className="g-4">
        {/* LEFT COLUMN: FORM CONTROLS */}
        <Col lg={5} md={12}>
          <div className="customizer-card">
            <div className="section-title">
              <RiSettings4Line className="text-primary font-size-16" />
              <span>Configure Selected Template Attributes</span>
            </div>

            {/* BRANDING: LOGO & SIGNATURE UPLOADER SECTION */}
            <div className="p-3 mb-4 rounded border bg-light">
              <div className="font-size-12 font-weight-bold text-dark mb-2.5 d-flex align-items-center gap-1.5">
                <RiImageAddLine className="text-primary font-size-15" />
                Default Brand Logo & Sign Image
              </div>

              <Row className="g-3">
                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    Clinic Header Logo
                  </Label>
                  {currentConfig.logoImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={currentConfig.logoImage}
                        alt="Logo Preview"
                        style={{ maxHeight: '42px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: '-8px', right: '-8px', width: '22px', height: '22px', padding: 0, borderRadius: '50%' }}
                        onClick={() => handleRemoveImage('logoImage')}
                        title="Remove Logo"
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload(e, 'logoImage')}
                      />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">PNG/JPG transparent</small>
                </Col>

                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    Doctor Signature / Stamp
                  </Label>
                  {currentConfig.signImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={currentConfig.signImage}
                        alt="Signature Preview"
                        style={{ maxHeight: '42px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: '-8px', right: '-8px', width: '22px', height: '22px', padding: 0, borderRadius: '50%' }}
                        onClick={() => handleRemoveImage('signImage')}
                        title="Remove Signature"
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> Upload Signature
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload(e, 'signImage')}
                      />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">Signature or seal stamp</small>
                </Col>
              </Row>
            </div>

            {/* PRESCRIPTION CUSTOMIZER */}
            {activeDocType === 'prescription' && (
              <div>
                <FormGroup>
                  <Label>Header Title Banner</Label>
                  <Input
                    type="text"
                    value={rxConfig.header_title || rxConfig.headerTitle || ''}
                    onChange={(e) => setRxConfig({ ...rxConfig, header_title: e.target.value, headerTitle: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Clinic Tagline & Subtitle</Label>
                  <Input
                    type="text"
                    value={rxConfig.tagline || ''}
                    onChange={(e) => setRxConfig({ ...rxConfig, tagline: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Theme Accent Color</Label>
                  <div className="d-flex align-items-center gap-2.5 flex-wrap mt-1">
                    {COLOR_PALETTES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        className="btn p-0 rounded-circle border shadow-2xs position-relative transition-all"
                        style={{
                          width: '28px',
                          height: '28px',
                          background: c.value,
                          transform: (rxConfig.accent_color === c.value || rxConfig.accentColor === c.value) ? 'scale(1.2)' : 'scale(1)',
                          border: (rxConfig.accent_color === c.value || rxConfig.accentColor === c.value) ? '2px solid #0f172a' : '1px solid #cbd5e1',
                        }}
                        onClick={() => setRxConfig({ ...rxConfig, accent_color: c.value, accentColor: c.value })}
                        title={c.name}
                      >
                        {(rxConfig.accent_color === c.value || rxConfig.accentColor === c.value) && (
                          <RiCheckLine className="text-white position-absolute" style={{ top: '4px', left: '4px' }} />
                        )}
                      </button>
                    ))}
                    <div
                      className="position-relative rounded-circle overflow-hidden border shadow-2xs d-flex align-items-center justify-content-center ms-1"
                      style={{
                        width: '28px',
                        height: '28px',
                        minWidth: '28px',
                        minHeight: '28px',
                        background: rxConfig.accent_color || rxConfig.accentColor || '#0284c7',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                      }}
                      title="Custom Color"
                    >
                      <input
                        type="color"
                        className="position-absolute"
                        style={{
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                        }}
                        value={rxConfig.accent_color || rxConfig.accentColor || '#0284c7'}
                        onChange={(e) => setRxConfig({ ...rxConfig, accent_color: e.target.value, accentColor: e.target.value })}
                      />
                      <RiPaletteLine className="text-white font-size-14 pointer-events-none" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.6))' }} />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Patient Directions & Advice Note</Label>
                  <Input
                    type="textarea"
                    rows={4}
                    value={rxConfig.general_advice || rxConfig.generalAdvice || ''}
                    onChange={(e) => setRxConfig({ ...rxConfig, general_advice: e.target.value, generalAdvice: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Footer Note / Disclaimer</Label>
                  <Input
                    type="text"
                    value={rxConfig.footer_note || rxConfig.footerNote || ''}
                    onChange={(e) => setRxConfig({ ...rxConfig, footer_note: e.target.value, footerNote: e.target.value })}
                  />
                </FormGroup>
              </div>
            )}

            {/* INVOICE CUSTOMIZER */}
            {activeDocType === 'invoice' && (
              <div>
                <FormGroup>
                  <Label>Invoice Header Title</Label>
                  <Input
                    type="text"
                    value={invConfig.header_title || invConfig.headerTitle || ''}
                    onChange={(e) => setInvConfig({ ...invConfig, header_title: e.target.value, headerTitle: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>GSTIN / Clinic Tax ID</Label>
                  <Input
                    type="text"
                    value={invConfig.gst_number || invConfig.gstNumber || ''}
                    onChange={(e) => setInvConfig({ ...invConfig, gst_number: e.target.value, gstNumber: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Theme Accent Color</Label>
                  <div className="d-flex align-items-center gap-2.5 flex-wrap mt-1">
                    {COLOR_PALETTES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        className="btn p-0 rounded-circle border shadow-2xs position-relative transition-all"
                        style={{
                          width: '28px',
                          height: '28px',
                          background: c.value,
                          transform: (invConfig.accent_color === c.value || invConfig.accentColor === c.value) ? 'scale(1.2)' : 'scale(1)',
                          border: (invConfig.accent_color === c.value || invConfig.accentColor === c.value) ? '2px solid #0f172a' : '1px solid #cbd5e1',
                        }}
                        onClick={() => setInvConfig({ ...invConfig, accent_color: c.value, accentColor: c.value })}
                        title={c.name}
                      >
                        {(invConfig.accent_color === c.value || invConfig.accentColor === c.value) && (
                          <RiCheckLine className="text-white position-absolute" style={{ top: '4px', left: '4px' }} />
                        )}
                      </button>
                    ))}
                    <div
                      className="position-relative rounded-circle overflow-hidden border shadow-2xs d-flex align-items-center justify-content-center ms-1"
                      style={{
                        width: '28px',
                        height: '28px',
                        minWidth: '28px',
                        minHeight: '28px',
                        background: invConfig.accent_color || invConfig.accentColor || '#0284c7',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                      }}
                      title="Custom Color"
                    >
                      <input
                        type="color"
                        className="position-absolute"
                        style={{
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                        }}
                        value={invConfig.accent_color || invConfig.accentColor || '#0284c7'}
                        onChange={(e) => setInvConfig({ ...invConfig, accent_color: e.target.value, accentColor: e.target.value })}
                      />
                      <RiPaletteLine className="text-white font-size-14 pointer-events-none" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.6))' }} />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Terms & Conditions</Label>
                  <Input
                    type="textarea"
                    rows={4}
                    value={invConfig.terms || ''}
                    onChange={(e) => setInvConfig({ ...invConfig, terms: e.target.value })}
                  />
                </FormGroup>
              </div>
            )}

            {/* RECEIPT CUSTOMIZER */}
            {activeDocType === 'receipt' && (
              <div>
                <FormGroup>
                  <Label>Receipt Header Title</Label>
                  <Input
                    type="text"
                    value={recConfig.header_title || recConfig.headerTitle || ''}
                    onChange={(e) => setRecConfig({ ...recConfig, header_title: e.target.value, headerTitle: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Acknowledgement Note</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={recConfig.notes || recConfig.thanksNote || ''}
                    onChange={(e) => setRecConfig({ ...recConfig, notes: e.target.value, thanksNote: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Theme Accent Color</Label>
                  <div className="d-flex align-items-center gap-2.5 flex-wrap mt-1">
                    {COLOR_PALETTES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        className="btn p-0 rounded-circle border shadow-2xs position-relative transition-all"
                        style={{
                          width: '28px',
                          height: '28px',
                          background: c.value,
                          transform: (recConfig.accent_color === c.value || recConfig.accentColor === c.value) ? 'scale(1.2)' : 'scale(1)',
                          border: (recConfig.accent_color === c.value || recConfig.accentColor === c.value) ? '2px solid #0f172a' : '1px solid #cbd5e1',
                        }}
                        onClick={() => setRecConfig({ ...recConfig, accent_color: c.value, accentColor: c.value })}
                        title={c.name}
                      >
                        {(recConfig.accent_color === c.value || recConfig.accentColor === c.value) && (
                          <RiCheckLine className="text-white position-absolute" style={{ top: '4px', left: '4px' }} />
                        )}
                      </button>
                    ))}
                    <div
                      className="position-relative rounded-circle overflow-hidden border shadow-2xs d-flex align-items-center justify-content-center ms-1"
                      style={{
                        width: '28px',
                        height: '28px',
                        minWidth: '28px',
                        minHeight: '28px',
                        background: recConfig.accent_color || recConfig.accentColor || '#059669',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                      }}
                      title="Custom Color"
                    >
                      <input
                        type="color"
                        className="position-absolute"
                        style={{
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                        }}
                        value={recConfig.accent_color || recConfig.accentColor || '#059669'}
                        onChange={(e) => setRecConfig({ ...recConfig, accent_color: e.target.value, accentColor: e.target.value })}
                      />
                      <RiPaletteLine className="text-white font-size-14 pointer-events-none" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.6))' }} />
                    </div>
                  </div>
                </FormGroup>
              </div>
            )}

            <div className="d-flex gap-2.5 mt-4">
              <Button color="primary" size="sm" className="font-size-12 flex-grow-1 font-weight-semibold shadow-sm py-2" onClick={handleSaveCurrent}>
                <RiSaveLine className="me-1" /> Save & Publish Template Settings
              </Button>
              <Button
                color="light"
                size="sm"
                className="font-size-12 border px-3 py-2"
                onClick={() => {
                  if (activeDocType === 'prescription') openPrescriptionPrintWindow(MOCK_RX_DATA, null, null, rxConfig);
                  else if (activeDocType === 'invoice') openInvoicePrintWindow(MOCK_INVOICE_DATA);
                  else openReceiptPrintWindow(MOCK_RECEIPT_DATA);
                }}
              >
                <RiPrinterLine className="me-1" /> Test Print
              </Button>
            </div>
          </div>
        </Col>

        {/* RIGHT COLUMN: REALISTIC PAPER SIMULATOR CANVAS */}
        <Col lg={7} md={12}>
          <div className="bg-white border rounded-3 p-3 shadow-2xs">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 bg-light p-2.5 rounded border">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-dark text-white font-size-11 px-2.5 py-1">
                  <RiEyeLine className="me-1" /> Live Canvas Simulator
                </span>
                <span className="badge bg-white text-muted border font-size-11">A4 Document Aspect</span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="btn-group btn-group-sm">
                  <Button
                    color={zoomLevel === 85 ? 'primary' : 'light'}
                    className="font-size-11 py-1 px-2.5"
                    onClick={() => setZoomLevel(85)}
                  >
                    85%
                  </Button>
                  <Button
                    color={zoomLevel === 100 ? 'primary' : 'light'}
                    className="font-size-11 py-1 px-2.5"
                    onClick={() => setZoomLevel(100)}
                  >
                    100%
                  </Button>
                </div>

                <Button
                  color="primary"
                  size="sm"
                  className="font-size-11 py-1.5 px-3 d-inline-flex align-items-center gap-1 shadow-sm font-weight-semibold"
                  onClick={() => {
                    if (activeDocType === 'prescription') openPrescriptionPrintWindow(MOCK_RX_DATA, null, null, rxConfig);
                    else if (activeDocType === 'invoice') openInvoicePrintWindow(MOCK_INVOICE_DATA);
                    else openReceiptPrintWindow(MOCK_RECEIPT_DATA);
                  }}
                >
                  <RiPrinterLine /> Print Document
                </Button>
              </div>
            </div>

            <div className="paper-canvas-outer">
              <div
                className="paper-sheet"
                style={{
                  borderTop: `5px solid ${activeDocType === 'prescription' ? (rxConfig.accent_color || rxConfig.accentColor) : (activeDocType === 'invoice' ? (invConfig.accent_color || invConfig.accentColor) : (recConfig.accent_color || recConfig.accentColor))}`,
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: 'top center',
                }}
              >
                <div className="paper-watermark">
                  {activeDocType === 'prescription' ? '℞' : 'PAID'}
                </div>

                {/* PRESCRIPTION PAPER VIEW */}
                {activeDocType === 'prescription' && (
                  <div className="d-flex flex-column justify-content-between h-100 position-relative" style={{ minHeight: '740px' }}>
                    <div>
                      <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                        <div>
                          {rxConfig.logoImage ? (
                            <img
                              src={rxConfig.logoImage}
                              alt="Clinic Logo"
                              className="mb-2"
                              style={{ maxHeight: '48px', maxWidth: '160px', objectFit: 'contain' }}
                            />
                          ) : (
                            <div className="d-inline-flex align-items-center gap-1 px-2 py-0.5 mb-1.5 rounded border border-dashed text-muted font-size-10 bg-light">
                              <RiImageAddLine className="font-size-12 text-primary" />
                              <span>Clinic Logo Space</span>
                            </div>
                          )}
                          <div className="d-flex align-items-center gap-2">
                            <h4 className="font-size-17 font-weight-bold mb-0" style={{ color: rxConfig.accent_color || rxConfig.accentColor }}>
                              909 Dental OPD & Implant Center
                            </h4>
                          </div>
                          <div className="font-size-11 font-weight-semibold text-secondary mt-0.5">{rxConfig.tagline}</div>
                          <div className="font-size-10 text-muted mt-1">
                            123 Koramangala 5th Block, Bangalore • Phone: 098220 11111 • Email: doctor@oralstop.com
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="font-size-13 font-weight-bold text-dark">{rxConfig.doctorName || 'Dr. Praveen Kumar, MDS'}</div>
                          <div className="font-size-10 text-muted">{rxConfig.doctorDeg || 'Oral & Maxillofacial Surgeon'}</div>
                          <div className="font-size-10 font-weight-bold" style={{ color: rxConfig.accent_color || rxConfig.accentColor }}>
                            Reg. No: {rxConfig.doctorReg || 'DCI-REG-88291'}
                          </div>
                        </div>
                      </div>

                      <div className="bg-light p-2 rounded border mb-3 font-size-11 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <span className="text-muted">Patient: </span>
                          <strong className="text-dark">Mrs. Sunita Reddy</strong> (34 Yrs, Female)
                        </div>
                        <div>
                          <span className="text-muted">Case ID: </span>
                          <strong className="text-dark">#909-P-101</strong>
                        </div>
                        <div>
                          <span className="text-muted">Date: </span>
                          <strong className="text-dark">{new Date().toLocaleDateString('en-GB')}</strong>
                        </div>
                      </div>

                      <div className="p-2 rounded font-size-11 mb-3 d-flex align-items-center gap-2" style={{ background: `${rxConfig.accent_color || rxConfig.accentColor}12`, borderLeft: `3px solid ${rxConfig.accent_color || rxConfig.accentColor}` }}>
                        <strong style={{ color: rxConfig.accent_color || rxConfig.accentColor }}>Clinical Diagnosis:</strong>
                        <span className="text-dark">{MOCK_RX_DATA.diagnosis}</span>
                      </div>

                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span style={{ fontSize: '24px', fontWeight: '900', color: rxConfig.accent_color || rxConfig.accentColor, fontFamily: 'Georgia, serif' }}>℞</span>
                        <span className="font-size-11 font-weight-bold text-dark text-uppercase tracking-wider">Prescribed Medications</span>
                      </div>

                      <div className="table-responsive rounded border bg-light mb-3">
                        <table className="table table-sm table-borderless mb-0 font-size-11">
                          <thead>
                            <tr className="bg-white border-bottom text-muted font-weight-bold">
                              <th className="py-1 px-2">#</th>
                              <th className="py-1 px-2">Medicine & Molecule</th>
                              <th className="py-1 px-2 text-center">Dosage</th>
                              <th className="py-1 px-2 text-center">Frequency</th>
                              <th className="py-1 px-2 text-center">Duration</th>
                              <th className="py-1 px-2 text-end">Instruction</th>
                            </tr>
                          </thead>
                          <tbody>
                            {MOCK_RX_DATA.medicines.map((m, idx) => (
                              <tr key={idx} className="border-bottom border-light">
                                <td className="py-1.5 px-2 text-muted font-weight-bold">{idx + 1}</td>
                                <td className="py-1.5 px-2">
                                  <strong className="text-dark">{m.medicine}</strong>
                                  <div className="font-size-10 text-muted">{m.molucule}</div>
                                </td>
                                <td className="py-1.5 px-2 text-center font-weight-semibold">{m.dose}</td>
                                <td className="py-1.5 px-2 text-center">
                                  <span className="badge font-size-10 px-2 py-0.5" style={{ background: `${rxConfig.accent_color || rxConfig.accentColor}15`, color: rxConfig.accent_color || rxConfig.accentColor, border: `1px solid ${rxConfig.accent_color || rxConfig.accentColor}30` }}>
                                    {m.frequent}
                                  </span>
                                </td>
                                <td className="py-1.5 px-2 text-center text-muted">{m.duration}</td>
                                <td className="py-1.5 px-2 text-end text-primary font-weight-semibold">{m.instructions}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="border-top pt-3 mt-4 d-flex justify-content-between align-items-end font-size-11">
                      <div className="text-muted" style={{ maxWidth: '340px', whiteSpace: 'pre-line' }}>
                        <strong className="text-dark d-block mb-1">Patient Instructions:</strong>
                        {rxConfig.general_advice || rxConfig.generalAdvice}
                        <div className="font-size-10 text-muted opacity-75 mt-2">{rxConfig.footer_note || rxConfig.footerNote}</div>
                      </div>
                      <div className="text-center d-flex flex-column align-items-center">
                        {rxConfig.signImage ? (
                          <img
                            src={rxConfig.signImage}
                            alt="Doctor Signature"
                            style={{ maxHeight: '75px', maxWidth: '180px', height: '68px', objectFit: 'contain', marginBottom: '4px' }}
                          />
                        ) : (
                          <div className="d-inline-flex flex-column align-items-center justify-content-center p-1.5 mb-1 rounded border border-dashed text-muted font-size-10 bg-light" style={{ width: '160px', height: '52px', borderColor: '#cbd5e1' }}>
                            <span className="font-weight-semibold" style={{ fontSize: '10px' }}>Doctor Sign & Stamp</span>
                            <span style={{ fontSize: '8.5px', opacity: 0.75 }}>Upload via Studio</span>
                          </div>
                        )}
                        <div className="border-top pt-1 font-weight-bold text-dark" style={{ width: '160px', marginTop: rxConfig.signImage ? '0' : '4px' }}>
                          {rxConfig.doctorName || 'Dr. Praveen Kumar'}
                        </div>
                        <div className="font-size-10 text-muted">Authorized Signature</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* INVOICE PAPER VIEW */}
                {activeDocType === 'invoice' && (
                  <div className="d-flex flex-column justify-content-between h-100 position-relative" style={{ minHeight: '740px' }}>
                    <div>
                      <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                        <div>
                          {invConfig.logoImage ? (
                            <img
                              src={invConfig.logoImage}
                              alt="Clinic Logo"
                              className="mb-2"
                              style={{ maxHeight: '48px', maxWidth: '160px', objectFit: 'contain' }}
                            />
                          ) : (
                            <div className="d-inline-flex align-items-center gap-1 px-2 py-0.5 mb-1.5 rounded border border-dashed text-muted font-size-10 bg-light">
                              <RiImageAddLine className="font-size-12 text-primary" />
                              <span>Clinic Logo Space</span>
                            </div>
                          )}
                          <div className="d-flex align-items-center gap-2">
                            <h4 className="font-size-17 font-weight-bold mb-0" style={{ color: invConfig.accent_color || invConfig.accentColor }}>
                              909 Dental OPD & Implant Center
                            </h4>
                          </div>
                          <div className="font-size-11 font-weight-semibold text-secondary mt-0.5">{invConfig.tagline}</div>
                          <div className="font-size-10 text-muted mt-1">
                            GSTIN: <strong>{invConfig.gst_number || invConfig.gstNumber}</strong> • Phone: 098220 11111
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="badge bg-light text-dark border font-size-11 px-2.5 py-1 mb-1">
                            {invConfig.header_title || invConfig.headerTitle}
                          </div>
                          <div className="font-size-13 font-weight-bold text-dark">#INV-2026-0891</div>
                          <div className="font-size-10 text-muted">Date: {new Date().toLocaleDateString('en-GB')}</div>
                        </div>
                      </div>

                      <div className="bg-light p-2 rounded border mb-3 font-size-11 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <span className="text-muted">Billed To: </span>
                          <strong className="text-dark">Mrs. Sunita Reddy</strong> (Case #909-P-101)
                        </div>
                        <div>
                          <span className="text-muted">Payment Status: </span>
                          <span className="badge bg-soft-success text-success font-weight-bold">PAID (UPI)</span>
                        </div>
                      </div>

                      <div className="table-responsive rounded border bg-light mb-3">
                        <table className="table table-sm table-borderless mb-0 font-size-11">
                          <thead>
                            <tr className="bg-white border-bottom text-muted font-weight-bold">
                              <th className="py-1 px-2">#</th>
                              <th className="py-1 px-2">Procedure / Dental Service</th>
                              <th className="py-1 px-2 text-end">Fee</th>
                              <th className="py-1 px-2 text-end">Discount</th>
                              <th className="py-1 px-2 text-end">Net Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-bottom border-light">
                              <td className="py-1.5 px-2 text-muted">1</td>
                              <td className="py-1.5 px-2">
                                <strong className="text-dark">Root Canal Treatment (RCT) (Tooth #16)</strong>
                              </td>
                              <td className="py-1.5 px-2 text-end">₹4,500.00</td>
                              <td className="py-1.5 px-2 text-end text-muted">₹0.00</td>
                              <td className="py-1.5 px-2 text-end font-weight-bold">₹4,500.00</td>
                            </tr>
                            <tr className="border-bottom border-light">
                              <td className="py-1.5 px-2 text-muted">2</td>
                              <td className="py-1.5 px-2">
                                <strong className="text-dark">Monolithic Zirconia Crown (Tooth #16)</strong>
                              </td>
                              <td className="py-1.5 px-2 text-end">₹12,000.00</td>
                              <td className="py-1.5 px-2 text-end text-danger">-₹1,000.00</td>
                              <td className="py-1.5 px-2 text-end font-weight-bold">₹11,000.00</td>
                            </tr>
                            <tr className="border-bottom border-light">
                              <td className="py-1.5 px-2 text-muted">3</td>
                              <td className="py-1.5 px-2">
                                <strong className="text-dark">Ultrasonic Scaling & Polishing</strong>
                              </td>
                              <td className="py-1.5 px-2 text-end">₹1,500.00</td>
                              <td className="py-1.5 px-2 text-end text-muted">₹0.00</td>
                              <td className="py-1.5 px-2 text-end font-weight-bold">₹1,500.00</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="p-2 rounded border bg-light font-size-10 text-muted flex-grow-1">
                          <strong className="text-dark d-block mb-1">🏦 Bank Transfer Details:</strong>
                          {invConfig.bank_name || 'HDFC Bank'} • A/C: <strong>{invConfig.account_no || '50200012345678'}</strong><br />
                          IFSC: <strong>{invConfig.ifsc_code || 'HDFC0001234'}</strong> ({invConfig.branch || 'Koramangala'})
                        </div>
                        <div className="p-2.5 rounded border bg-light font-size-11" style={{ width: '220px' }}>
                          <div className="d-flex justify-content-between border-top pt-1 font-size-12 font-weight-bold text-dark">
                            <span>Net Paid:</span>
                            <span style={{ color: invConfig.accent_color || invConfig.accentColor }}>₹17,000.00</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-top pt-3 d-flex justify-content-between align-items-end font-size-11">
                      <div className="text-muted" style={{ maxWidth: '340px', whiteSpace: 'pre-line' }}>
                        <strong className="text-dark d-block mb-1">Terms & Conditions:</strong>
                        {invConfig.terms}
                      </div>
                      <div className="text-center d-flex flex-column align-items-center">
                        {invConfig.signImage ? (
                          <img
                            src={invConfig.signImage}
                            alt="Authorized Signatory"
                            style={{ maxHeight: '75px', maxWidth: '180px', height: '68px', objectFit: 'contain', marginBottom: '4px' }}
                          />
                        ) : (
                          <div className="d-inline-flex flex-column align-items-center justify-content-center p-1.5 mb-1 rounded border border-dashed text-muted font-size-10 bg-light" style={{ width: '160px', height: '52px', borderColor: '#cbd5e1' }}>
                            <span className="font-weight-semibold" style={{ fontSize: '10px' }}>Clinic Stamp & Sign</span>
                            <span style={{ fontSize: '8.5px', opacity: 0.75 }}>Upload via Studio</span>
                          </div>
                        )}
                        <div className="border-top pt-1 font-weight-bold text-dark" style={{ width: '160px', marginTop: invConfig.signImage ? '0' : '4px' }}>
                          {invConfig.signatory_title || invConfig.signatoryTitle || 'Accounts Desk'}
                        </div>
                        <div className="font-size-10 text-muted">Authorized Signatory</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* RECEIPT PAPER VIEW */}
                {activeDocType === 'receipt' && (
                  <div className="d-flex flex-column justify-content-between h-100 position-relative" style={{ minHeight: '520px' }}>
                    <div>
                      <div className="text-center border-bottom pb-3 mb-3">
                        {recConfig.logoImage ? (
                          <img
                            src={recConfig.logoImage}
                            alt="Clinic Logo"
                            className="mb-2"
                            style={{ maxHeight: '42px', maxWidth: '140px', objectFit: 'contain' }}
                          />
                        ) : (
                          <div className="d-inline-flex align-items-center gap-1 px-2.5 py-0.5 mb-1.5 rounded border border-dashed text-muted font-size-10 bg-light">
                            <RiImageAddLine className="font-size-12 text-primary" />
                            <span>Clinic Logo Space</span>
                          </div>
                        )}
                        <h4 className="font-size-18 font-weight-bold mb-0.5" style={{ color: recConfig.accent_color || recConfig.accentColor }}>
                          909 Dental OPD & Implant Center
                        </h4>
                        <div className="font-size-11 font-weight-semibold text-secondary">{recConfig.tagline}</div>
                        <div className="badge bg-light text-dark border font-size-11 px-3 py-1 mt-2">
                          {recConfig.header_title || recConfig.headerTitle}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between font-size-11 mb-3">
                        <div>
                          <span className="text-muted">Receipt No: </span>
                          <strong className="text-dark">#REC-909-402</strong>
                        </div>
                        <div>
                          <span className="text-muted">Date: </span>
                          <strong className="text-dark">{new Date().toLocaleDateString('en-GB')}</strong>
                        </div>
                      </div>

                      <div className="bg-light p-3 rounded border mb-3 font-size-11 d-flex flex-column gap-2">
                        <div>
                          <span className="text-muted">Received With Thanks From:</span>
                          <div className="font-weight-bold text-dark font-size-13">Mrs. Sunita Reddy (Case #909-P-101)</div>
                        </div>
                        <div>
                          <span className="text-muted">Payment Mode:</span>
                          <div className="font-weight-semibold text-dark">UPI (GPay / PhonePe) • Reference: INV-2026-0891</div>
                        </div>
                        <div className="border-top pt-2 d-flex justify-content-between align-items-center">
                          <span className="font-weight-bold text-dark">Amount Received:</span>
                          <span className="font-size-16 font-weight-bold" style={{ color: recConfig.accent_color || recConfig.accentColor }}>
                            ₹17,000.00
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-top pt-3 d-flex justify-content-between align-items-end font-size-11">
                      <div className="text-muted" style={{ maxWidth: '320px', whiteSpace: 'pre-line' }}>
                        {recConfig.footer_note || recConfig.footerNote}
                      </div>
                      <div className="text-center d-flex flex-column align-items-center">
                        {recConfig.signImage ? (
                          <img
                            src={recConfig.signImage}
                            alt="Cashier Stamp"
                            style={{ maxHeight: '75px', maxWidth: '170px', height: '65px', objectFit: 'contain', marginBottom: '4px' }}
                          />
                        ) : (
                          <div className="d-inline-flex flex-column align-items-center justify-content-center p-1.5 mb-1 rounded border border-dashed text-muted font-size-10 bg-light" style={{ width: '160px', height: '52px', borderColor: '#cbd5e1' }}>
                            <span className="font-weight-semibold" style={{ fontSize: '10px' }}>Cashier Stamp & Sign</span>
                            <span style={{ fontSize: '8.5px', opacity: 0.75 }}>Upload via Studio</span>
                          </div>
                        )}
                        <div className="border-top pt-1 font-weight-bold text-dark" style={{ width: '160px', marginTop: recConfig.signImage ? '0' : '4px' }}>
                          {recConfig.signatory_title || recConfig.signatoryTitle || 'Reception Desk'}
                        </div>
                        <div className="font-size-10 text-muted">Cashier Stamp</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TemplateStudio;
